/**
 * Phase 45 Plan 45-07 (D-18) — atomic claim with optimistic UI + rollback.
 *
 * Semantics per UI-SPEC §Claim interaction (lines 565-569):
 *
 *   onMutate    — snapshot the ['tasks','unassigned'] cache + optimistically
 *                 REMOVE the row being claimed. Return the snapshot as the
 *                 rollback context.
 *   onError     — restore the snapshot. If status === 409 surface the toast
 *                 "Someone else just claimed this task."; 403 surfaces
 *                 "Cannot claim this task."; everything else surfaces
 *                 "Couldn't claim this task. Try again."
 *   onSuccess   — invalidate ['tasks','mine'], ['tasks','unassigned'],
 *                 ['manager','portfolio'] (per CONTEXT Integration Points §).
 *
 * Backend contract (Plan 45-03): POST /api/tasks/:taskId/claim atomically
 * sets assigneeId = caller AND if status ∈ (Triage, Backlog) transitions
 * to Open. 409 ConflictError surfaces when assigneeId is already non-null
 * (race resolution).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { claimTask } from '../services/api/tasks';
import type { UnassignedTaskDto } from '../services/api/tasks';
import { ApiClientError } from '../services/api/client';
import { useToast } from '../components/ui/Toast';
import { useAuthOptional } from '../services/authContext';
import { UNASSIGNED_TASKS_BASE_KEY } from './useUnassignedTasks';
import { MY_TASKS_BASE_KEY } from './useMyTasks';

interface ClaimMutationContext {
  previous: UnassignedTaskDto[] | undefined;
}

function statusOf(err: unknown): number | undefined {
  if (err instanceof ApiClientError) return err.statusCode;
  if (err && typeof err === 'object' && 'statusCode' in err) {
    const s = (err as { statusCode?: unknown }).statusCode;
    return typeof s === 'number' ? s : undefined;
  }
  if (err && typeof err === 'object' && 'status' in err) {
    const s = (err as { status?: unknown }).status;
    return typeof s === 'number' ? s : undefined;
  }
  return undefined;
}

export function useClaimTask() {
  const queryClient = useQueryClient();
  const toast = useToast();
  // Phase 45 follow-up (merge to main 2026-05-22) — match useUnassignedTasks's
  // persona-scoped query key so optimistic UI hits the right cache entry. The
  // BASE_KEY is still used as the invalidation prefix in onSuccess (TanStack
  // Query prefix-matches invalidateQueries) but exact-match operations
  // (cancelQueries / getQueryData / setQueryData) need the full scoped key.
  const auth = useAuthOptional();
  const effectiveUserId = auth?.impersonatedUser?.id ?? auth?.user?.id ?? null;
  const unassignedScopedKey = [...UNASSIGNED_TASKS_BASE_KEY, effectiveUserId];

  return useMutation<
    { task: unknown; transitioned: boolean },
    Error,
    number,
    ClaimMutationContext
  >({
    mutationFn: (taskId) => claimTask(taskId),
    onMutate: async (taskId) => {
      await queryClient.cancelQueries({ queryKey: unassignedScopedKey });
      const previous = queryClient.getQueryData<UnassignedTaskDto[]>(unassignedScopedKey);
      if (previous) {
        queryClient.setQueryData<UnassignedTaskDto[]>(
          unassignedScopedKey,
          previous.filter((t) => t.taskId !== taskId)
        );
      }
      return { previous };
    },
    onError: (err, _taskId, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(unassignedScopedKey, context.previous);
      }
      const status = statusOf(err);
      if (status === 409) {
        toast('Someone else just claimed this task.', 'error');
      } else if (status === 403) {
        toast('Cannot claim this task.', 'error');
      } else {
        toast("Couldn't claim this task. Try again.", 'error');
      }
    },
    onSuccess: (_data, taskId) => {
      queryClient.invalidateQueries({ queryKey: MY_TASKS_BASE_KEY });
      queryClient.invalidateQueries({ queryKey: UNASSIGNED_TASKS_BASE_KEY });
      queryClient.invalidateQueries({ queryKey: ['manager', 'portfolio'] });
      // 260806-ddg — claiming auto-subscribes the claimer as a watcher.
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'watchers'] });
    },
  });
}
